/**
 * `Content-Disposition`, WRITTEN AND READ, in one place because it now travels both ways.
 *
 * It is the response header that tells a browser to save an attachment under its own name, and
 * — since the upload path became a raw body — the request header that carries that name up in
 * the first place. One module, so the encoding and the decoding cannot drift apart: a filename
 * that round-trips is the only thing either half is for.
 *
 * IN `@portal/shared` BECAUSE ALL THREE CALLERS NEED IT: the API writes it on a download and
 * reads it on an upload, the SPA writes it on an upload, and the MCP dispatcher does both. It
 * is a pure string function over a string, which is what this package is for and what makes it
 * browser-safe by construction.
 *
 * ONLY THE RFC 6266 `filename*` FORM, in both directions. The bare `filename=` parameter cannot
 * carry anything outside ISO-8859-1, and supporting both means emitting one that has to be
 * escaped by a second set of rules and parsing one that has to be unescaped by them.
 */
/**
 * The header value for a name, always as an attachment.
 *
 * ALWAYS `attachment`, NEVER `inline`. Together with `servedMediaType` this is what keeps a
 * file the portal declined to identify from being rendered by the browser that fetched it.
 *
 * CR and LF are stripped BEFORE encoding rather than trusted to it, because a filename is the
 * classic header-injection carrier and this is the one place a filename reaches a header at
 * all. The extra escapes past `encodeURIComponent` are the characters RFC 5987 excludes from
 * `attr-char` that it happens to leave alone.
 */
export function contentDisposition(filename) {
    // biome-ignore lint/suspicious/noControlCharactersInRegex: stripping controls is the point.
    const stripped = filename.replace(/[ -]/g, '');
    const encoded = encodeURIComponent(stripped).replace(/['()*]/g, (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`);
    return `attachment; filename*=UTF-8''${encoded}`;
}
/**
 * The name out of a header, or null.
 *
 * Null covers every failure — absent header, no `filename*` parameter, a malformed
 * percent-sequence — because the two callers both have something better to do with a missing
 * name than throw: the upload route hands it to a schema that answers 400 with the field
 * named, and the MCP read path falls back to the attachment id.
 */
export function filenameFromDisposition(header) {
    if (header === undefined || header === null)
        return null;
    const match = /filename\*=UTF-8''([^;]+)/i.exec(header);
    if (match?.[1] === undefined)
        return null;
    try {
        const decoded = decodeURIComponent(match[1]).trim();
        return decoded === '' ? null : decoded;
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=content-disposition.js.map